import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, UserMinus, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

const STAGES = [
  "Manager Approval", "HR Discussion", "Knowledge Transfer",
  "Asset Return", "Finance Clearance", "Exit Interview", "FNF Settlement",
];

const EXIT_TYPES = [
  { value: "resignation", label: "Resignation" },
  { value: "termination", label: "Termination" },
  { value: "retirement", label: "Retirement" },
  { value: "absconding", label: "Absconding" },
];

const CAN_INITIATE = ["super_admin", "hr", "manager"];

export default function Exit() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    employee_id: "",
    resignation_date: new Date().toISOString().slice(0, 10),
    last_working_day: "",
    reason: "",
    exit_type: "resignation",
    notes: "",
  });

  const canInitiate = CAN_INITIATE.includes(user?.role);

  useEffect(() => {
    if (open && employees.length === 0) {
      api.get("/employees").then((r) =>
        setEmployees((r.data || []).filter((e) => !e.exit_in_progress && e.status !== "exited"))
      );
    }
  }, [open, employees.length]);

  const resetForm = () => {
    setForm({
      employee_id: "",
      resignation_date: new Date().toISOString().slice(0, 10),
      last_working_day: "",
      reason: "",
      exit_type: "resignation",
      notes: "",
    });
  };

  const submit = async () => {
    if (!form.employee_id) { toast.error("Please select an employee"); return; }
    if (!form.last_working_day) { toast.error("Last working day is required"); return; }
    if (!form.reason || form.reason.trim().length < 3) { toast.error("Please enter a reason"); return; }

    setSubmitting(true);
    try {
      await api.post("/exits/initiate", form);
      toast.success("Exit initiated. Notification sent to the employee.");
      setOpen(false);
      resetForm();
      setEmployees([]);  // force refetch next open
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to initiate exit");
    } finally {
      setSubmitting(false);
    }
  };

  const advance = async (row) => {
    try {
      await api.post(`/exits/${row.id}/advance`);
      toast.success("Stage advanced");
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to advance");
    }
  };

  return (
    <div data-testid="exit-page">
      <PageHeader
        title="Exit Management"
        subtitle={
          canInitiate
            ? "Initiate and track resignations through to full and final settlement."
            : "Track resignations through to full and final settlement."
        }
        action={
          canInitiate ? (
            <Button
              data-testid="initiate-exit"
              onClick={() => setOpen(true)}
              className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Initiate exit
            </Button>
          ) : null
        }
      />

      <div className="mb-6 hr-card p-5">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-3">7-step workflow</div>
        <div className="flex flex-wrap gap-2">
          {STAGES.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-[#F26B3A]/10 text-[#0F172A] grid place-items-center text-xs font-semibold">{i + 1}</div>
              <span className="text-xs text-gray-700">{s}</span>
              {i < STAGES.length - 1 && <span className="text-gray-300 mx-1">→</span>}
            </div>
          ))}
        </div>
      </div>

      <DataTable
        testId="exits-table"
        endpoint="/exits"
        refreshKey={refresh}
        columns={[
          { key: "employee_name", label: "Employee" },
          { key: "employee_id", label: "ID", mono: true },
          { key: "exit_type", label: "Type", render: (r) => (
            <span className="capitalize text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
              {r.exit_type || "resignation"}
            </span>
          )},
          { key: "resignation_date", label: "Initiated" },
          { key: "last_working_day", label: "Last day" },
          { key: "reason", label: "Reason" },
          { key: "stage", label: "Stage", badge: true },
          { key: "status", label: "Status", badge: true },
          ...(canInitiate ? [{
            key: "actions",
            label: "Action",
            render: (r) => (
              r.status !== "completed" && r.status !== "clearance_complete" ? (
                <Button
                  data-testid={`advance-exit-${r.id}`}
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={(e) => { e.stopPropagation(); advance(r); }}
                >
                  <ChevronRight className="h-3.5 w-3.5 mr-0.5" /> Advance
                </Button>
              ) : (
                <span className="text-[11px] text-emerald-700">Cleared</span>
              )
            ),
          }] : []),
        ]}
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-lg" data-testid="initiate-exit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserMinus className="h-5 w-5 text-[#F26B3A]" /> Initiate exit formalities
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="exit-employee-select" className="mt-1.5">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees.map((e) => (
                    <SelectItem
                      key={e.employee_id || e.id}
                      value={e.employee_id || ""}
                      data-testid={`exit-employee-option-${e.employee_id}`}
                    >
                      {e.name} · <span className="text-gray-400 font-mono text-xs">{e.employee_id}</span> · {e.department}
                    </SelectItem>
                  ))}
                  {employees.length === 0 && (
                    <div className="px-3 py-6 text-xs text-gray-500 text-center">
                      No active employees available.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Exit type</Label>
                <Select value={form.exit_type} onValueChange={(v) => setForm({ ...form, exit_type: v })}>
                  <SelectTrigger data-testid="exit-type-select" className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXIT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`exit-type-${t.value}`}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Resignation date</Label>
                <Input
                  data-testid="exit-resignation-date"
                  type="date"
                  value={form.resignation_date}
                  onChange={(e) => setForm({ ...form, resignation_date: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Last working day</Label>
              <Input
                data-testid="exit-last-working-day"
                type="date"
                value={form.last_working_day}
                onChange={(e) => setForm({ ...form, last_working_day: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Reason</Label>
              <Input
                data-testid="exit-reason"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Personal reasons, Better opportunity, Higher studies"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Notes (optional)</Label>
              <Textarea
                data-testid="exit-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Internal notes for HR / Manager visibility"
                className="mt-1.5"
              />
            </div>

            <div className="text-[11px] text-gray-500 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Initiating an exit will start the 7-step clearance workflow and email the employee.
              This action will be logged against your account ({user?.email}).
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" data-testid="exit-cancel" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="exit-submit"
              onClick={submit}
              disabled={submitting}
              className="bg-[#F26B3A] hover:bg-[#d85d2f]"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Initiate exit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
